import * as XLSX from 'xlsx';

export interface CSVParseResult {
  success: boolean;
  data: any[];
  errors: string[];
}

export const parseFile = async (file: File): Promise<CSVParseResult> => {
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  
  if (fileExtension === '.xlsx') {
    return parseXLSX(file);
  } else {
    const csvContent = await file.text();
    return parseCSV(csvContent);
  }
};

export const parseXLSX = async (file: File): Promise<CSVParseResult> => {
  const errors: string[] = [];
  const data: any[] = [];
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    let jsonData;
    try {
      jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        raw: false,
        dateNF: 'dd-mm-yyyy'
      });
    } catch (error) {
      jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        raw: true
      });
    }
    
    if (jsonData.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['XLSX file must contain at least a header row and one data row']
      };
    }

    const headers = (jsonData[0] as string[]).map(h => h?.toString().trim().toLowerCase().replace(/"/g, '') || '');

    const headerMapping: { [key: string]: string } = {
      'first name': 'first_name',
      'firstname': 'first_name',
      'first_name': 'first_name',
      'last name': 'last_name',
      'lastname': 'last_name',
      'last_name': 'last_name',
      'email': 'email',
      'phone': 'phone',
      'phone number': 'phone',
      'phone_number': 'phone',
      'subscription end': 'subscription_end',
      'subscription_end': 'subscription_end',
      'expiration date': 'subscription_end',
      'expiration_date': 'subscription_end',
      'exp date': 'subscription_end',
      'exp_date': 'subscription_end'
    };

    const columnIndices: { [key: string]: number } = {};
    Object.keys(headerMapping).forEach(header => {
      const index = headers.findIndex(h => h === header);
      if (index !== -1) {
        columnIndices[headerMapping[header]] = index;
      }
    });

    const requiredColumns = ['first_name', 'last_name', 'email', 'phone', 'subscription_end'];
    const missingColumns = requiredColumns.filter(col => columnIndices[col] === undefined);
    
    if (missingColumns.length > 0) {
      return {
        success: false,
        data: [],
        errors: [`Missing required columns: ${missingColumns.join(', ')}`]
      };
    }

    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] as any[];
      
      if (!row || row.length === 0) continue;
      
      const member: any = {
        first_name: row[columnIndices.first_name]?.toString().trim() || '',
        last_name: row[columnIndices.last_name]?.toString().trim() || '',
        email: row[columnIndices.email]?.toString().trim() || '',
        phone: row[columnIndices.phone]?.toString().trim() || '',
        subscription_end: row[columnIndices.subscription_end]?.toString().trim() || ''
      };

      const rowErrors: string[] = [];
      if (!member.first_name) rowErrors.push('First name is required');
      if (!member.last_name) rowErrors.push('Last name is required');
      if (!member.email) rowErrors.push('Email is required');
      if (!member.phone) rowErrors.push('Phone is required');
      if (!member.subscription_end) rowErrors.push('Subscription end date is required');

      if (member.email && !isValidEmail(member.email)) {
        rowErrors.push('Invalid email format');
      }

      if (member.phone && !isValidPhone(member.phone)) {
        rowErrors.push('Invalid phone format');
      }

      if (member.subscription_end && !isValidDate(member.subscription_end)) {
        rowErrors.push('Invalid date format');
      }

      if (rowErrors.length > 0) {
        errors.push(`Row ${i + 1}: ${rowErrors.join(', ')}`);
      } else {

        if (member.phone && !member.phone.startsWith('+')) {
          member.phone = '+' + member.phone;
        }
        
        if (member.subscription_end) {
          try {
            const date = convertToDate(member.subscription_end);
            if (date && !isNaN(date.getTime())) {
              member.subscription_end = date.toISOString();
            }
          } catch {
          }
        }
        
        data.push(member);
      }
    }

    return {
      success: errors.length === 0,
      data,
      errors
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      errors: ['Failed to parse XLSX file: ' + (error as Error).message]
    };
  }
};

export const parseCSV = (csvContent: string): CSVParseResult => {
  const errors: string[] = [];
  const data: any[] = [];
  
  try {
    const lines = csvContent.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['CSV file must contain at least a header row and one data row']
      };
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

    const headerMapping: { [key: string]: string } = {
      'first name': 'first_name',
      'firstname': 'first_name',
      'first_name': 'first_name',
      'last name': 'last_name',
      'lastname': 'last_name',
      'last_name': 'last_name',
      'email': 'email',
      'phone': 'phone',
      'phone number': 'phone',
      'phone_number': 'phone',
      'subscription end': 'subscription_end',
      'subscription_end': 'subscription_end',
      'expiration date': 'subscription_end',
      'expiration_date': 'subscription_end',
      'exp date': 'subscription_end',
      'exp_date': 'subscription_end'
    };

    const columnIndices: { [key: string]: number } = {};
    Object.keys(headerMapping).forEach(header => {
      const index = headers.findIndex(h => h === header);
      if (index !== -1) {
        columnIndices[headerMapping[header]] = index;
      }
    });
    const requiredColumns = ['first_name', 'last_name', 'email', 'phone', 'subscription_end'];
    const missingColumns = requiredColumns.filter(col => columnIndices[col] === undefined);
    
    if (missingColumns.length > 0) {
      return {
        success: false,
        data: [],
        errors: [`Missing required columns: ${missingColumns.join(', ')}`]
      };
    }

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i];
      const values = parseCSVRow(row);
      
      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const member: any = {
        first_name: values[columnIndices.first_name]?.trim() || '',
        last_name: values[columnIndices.last_name]?.trim() || '',
        email: values[columnIndices.email]?.trim() || '',
        phone: values[columnIndices.phone]?.trim() || '',
        subscription_end: values[columnIndices.subscription_end]?.trim() || ''
      };
      const rowErrors: string[] = [];
      if (!member.first_name) rowErrors.push('First name is required');
      if (!member.last_name) rowErrors.push('Last name is required');
      if (!member.email) rowErrors.push('Email is required');
      if (!member.phone) rowErrors.push('Phone is required');
      if (!member.subscription_end) rowErrors.push('Subscription end date is required');

      if (member.email && !isValidEmail(member.email)) {
        rowErrors.push('Invalid email format');
      }

      if (member.phone && !isValidPhone(member.phone)) {
        rowErrors.push('Invalid phone format');
      }

      if (member.subscription_end && !isValidDate(member.subscription_end)) {
        rowErrors.push('Invalid date format');
      }

      if (rowErrors.length > 0) {
        errors.push(`Row ${i + 1}: ${rowErrors.join(', ')}`);
      } else {

        if (member.phone && !member.phone.startsWith('+')) {
          member.phone = '+' + member.phone;
        }
        
        if (member.subscription_end) {
          try {
            const date = convertToDate(member.subscription_end);
            if (date && !isNaN(date.getTime())) {
              member.subscription_end = date.toISOString();
            }
          } catch {
          }
        }
        
        data.push(member);
      }
    }

    return {
      success: errors.length === 0,
      data,
      errors
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      errors: ['Failed to parse CSV file: ' + (error as Error).message]
    };
  }
};

const parseCSVRow = (row: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
};

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[+]?[\d\s\-()]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

const convertToDate = (dateString: string): Date | null => {
  if (!dateString || dateString.trim() === '') {
    return null;
  }
  
  try {
    let dateToConvert = dateString.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateToConvert)) {
      const parts = dateToConvert.split('-');
      dateToConvert = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    if (/^\d+$/.test(dateToConvert)) {
      const excelDate = parseInt(dateToConvert);
      if (excelDate > 25569) {
        const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
        return jsDate;
      }
    }
    
    const date = new Date(dateToConvert);
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch (error) {
    return null;
  }
};

const isValidDate = (dateString: string): boolean => {
  const date = convertToDate(dateString);
  return date !== null && !isNaN(date.getTime());
};
